import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { ArrowLeft, CreditCard, Wallet, PaypalLogo, Barcode, DeviceMobile, CheckCircle, Warning, SpinnerGap, Receipt, Copy, Check } from "@phosphor-icons/react";
import { myApi } from "../api";
import { isCompanyNif } from "@/shared/checkout/nif";
import { formatMbwayPhone, isValidMbwayPhone } from "@/shared/checkout/mbway";
import PaymentMethodSelector from "../components/shop/checkout/PaymentMethodSelector";
import MyOrderPaymentPaypal from "../components/MyOrderPaymentPaypal";

const CURRENCY_SYMBOLS = { EUR: "€", USD: "$", BRL: "R$", GBP: "£" };
const fmtMoney = (v, cur = "EUR") => {
  const n = Number(v || 0);
  const sym = CURRENCY_SYMBOLS[cur] || cur;
  return `${n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${sym}`;
};

/**
 * /my/orders/:hash/payment — Página de pagamento da encomenda.
 *
 * Espelha o UX do shop legacy (my.ihavethepower.net/Encomendas → "Alterar"):
 *  • Resumo da encomenda (total, morada de facturação, NIF).
 *  • Detalhes do método actual (ref MB, telemóvel MBWay, link PayPal, link
 *    EasyPay cartão de crédito, link Cofidis Pay).
 *  • Permite ao cliente alterar o método se ainda estiver em AP/CC.
 */
export default function MyOrderPayment() {
  const { hash } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });
  const [changing, setChanging] = useState(false);
  const [changeError, setChangeError] = useState("");
  const [newMethod, setNewMethod] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [methods, setMethods] = useState([]);
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState("");

  const loadPaymentInfo = async () => {
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const [r, pm] = await Promise.all([
        myApi.shopPaymentByHash(hash),
        myApi.shopPaymentMethods().catch(() => []),
      ]);
      if (r?.success === false) {
        setState({ loading: false, data: null, error: r.message || "Encomenda não encontrada." });
        return;
      }
      const pmArr = Array.isArray(pm) ? pm : (pm?.items || pm?.obj || []);
      setMethods(pmArr);
      setState({ loading: false, data: r, error: "" });
      setNewMethod(r?.paymentData?.ID_TipoPagamento || "");
    } catch (e) {
      setState({ loading: false, data: null, error: e.message || "Erro a carregar detalhes do pagamento." });
    }
  };

  useEffect(() => { loadPaymentInfo(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [hash]);

  if (state.loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center">
        <SpinnerGap size={32} weight="bold" className="animate-spin text-[#0F1829]" />
      </div>
    );
  }

  if (state.error || !state.data) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="my-order-payment-error">
        <Link to="/my/encomendas" className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#0F1829]">
          <ArrowLeft size={16} /> Voltar às encomendas
        </Link>
        <div className="mt-4 bg-[#FEF2F2] border border-[#FECACA] rounded-xl p-6 text-[#991B1B]">
          <Warning size={24} weight="fill" className="mb-2" />
          <div className="font-semibold">Erro</div>
          <div className="text-sm mt-1">{state.error || "Encomenda não encontrada."}</div>
        </div>
      </div>
    );
  }

  const pd = state.data.paymentData || {};
  const orderId = state.data.ID_Encomenda;
  const cur = pd.ID_Moeda || "EUR";
  const total = Number(pd.PrecoFinal || pd.PrecoTotal || 0);
  const tipoPag = pd.ID_TipoPagamento || "";
  const estado = pd.ID_EstadoEncomenda || "";
  const alteravel = pd.Alteravel === "S";
  const canChangeMethod = alteravel && ["AP", "CC"].includes(estado);

  // Re-dispara `changePayment` no legacy para tentar gerar URL externo (PP/CT/CP).
  // Usado quando `getOrderPayment` devolve URL vazio — situação típica quando
  // a chamada inicial à EuPago/PayPal/EasyPay no checkout legacy falhou
  // silenciosamente (timeout, indisponibilidade, etc.).
  const handleRegenerate = async () => {
    setRegenError("");
    setRegenerating(true);
    try {
      const r = await myApi.shopPaymentChange({
        HashEncomenda: hash,
        ID_TipoPagamento: tipoPag,
        NumeroTelemovelPagamento: "",
        NumParcelas: 1,
      });
      if (r?.success === false) {
        setRegenError(r.message || "Não foi possível gerar o link.");
        return;
      }
      await loadPaymentInfo();
    } catch (e) {
      setRegenError(e.message || "Erro ao gerar link.");
    } finally {
      setRegenerating(false);
    }
  };

  const handleChange = async () => {
    setChangeError("");
    if (!newMethod) { setChangeError("Seleccione um método de pagamento."); return; }
    if (newMethod === "MW") {
      if (!(newPhone || "").trim()) {
        setChangeError("Introduza o número de telemóvel para MBWay.");
        return;
      }
      if (!isValidMbwayPhone(newPhone)) {
        setChangeError("Para MBWay indique o número de telemóvel com prefixo internacional (ex: 351 934751347).");
        return;
      }
    }
    if (newMethod === "CP") {
      if (total < 60 || total > 2500) { setChangeError("Cofidis Pay: o valor total tem de estar entre 60 € e 2500 €."); return; }
      if (isCompanyNif(pd.nifFaturacao)) {
        setChangeError("Cofidis Pay não está disponível para NIFs de empresa.");
        return;
      }
    }
    setChanging(true);
    try {
      const r = await myApi.shopPaymentChange({
        HashEncomenda: hash,
        ID_TipoPagamento: newMethod,
        NumeroTelemovelPagamento: newMethod === "MW" ? formatMbwayPhone(newPhone) : "",
        NumParcelas: 1,
      });
      if (r?.success === false) { setChangeError(r.message || "Erro ao alterar método."); return; }
      await loadPaymentInfo();
    } catch (e) {
      setChangeError(e.message || "Erro ao alterar método.");
    } finally {
      setChanging(false);
    }
  };

  return (
    <div className="min-h-full bg-[#F9FAFB] py-8 px-4 sm:px-6 lg:px-8" style={{ fontFamily: "'Manrope', sans-serif" }} data-testid="my-order-payment">
      <div className="max-w-3xl mx-auto">
        <Link to="/my/encomendas" className="inline-flex items-center gap-1 text-sm text-[#6B7280] hover:text-[#0F1829] mb-4" data-testid="my-order-payment-back">
          <ArrowLeft size={16} /> Voltar às encomendas
        </Link>

        <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 mb-5">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Encomenda</div>
              <div className="text-2xl font-bold text-[#0F1829] mt-1" data-testid="my-order-payment-id">#{orderId}</div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Total</div>
              <div className="text-2xl font-bold text-[#0F1829] mt-1" data-testid="my-order-payment-total">{fmtMoney(total, cur)}</div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
            <div>
              <div className="text-[#6B7280]">Facturação</div>
              <div className="text-[#111827] font-medium">{pd.nomeFaturacao}</div>
              <div className="text-[#6B7280]">{pd.mFaturacao}, {pd.codigoPostalFaturacao} {pd.localidadeFaturacao}</div>
              {pd.nifFaturacao ? <div className="text-[#6B7280]">NIF: {pd.nifFaturacao}</div> : null}
            </div>
            {pd.ID_TipoTransporte && pd.ID_TipoTransporte !== "NA" ? (
              <div>
                <div className="text-[#6B7280]">Envio</div>
                <div className="text-[#111827] font-medium">{pd.nomeEnvio}</div>
                <div className="text-[#6B7280]">{pd.mEnvio}, {pd.codigoPostalEnvio} {pd.localidadeEnvio}</div>
                <div className="text-[#6B7280] mt-1">{pd.descricaoTipoTransporte}</div>
              </div>
            ) : null}
          </div>
        </div>

        <PaymentDetails tipoPag={tipoPag} pd={pd} estado={estado} total={total} cur={cur}
          orderId={orderId} hashEncomenda={hash}
          onRegenerate={handleRegenerate} regenerating={regenerating} regenError={regenError}
          onPaymentConfirmed={loadPaymentInfo} />

        {canChangeMethod ? (
          <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 mt-5" data-testid="my-order-payment-change-section">
            <h3 className="text-base font-semibold text-[#0F1829] mb-1">Alterar método de pagamento</h3>
            <p className="text-sm text-[#6B7280] mb-4">Pode trocar para outro método desde que a encomenda ainda esteja por pagar.</p>
            <PaymentMethodSelector
              methods={methods}
              selected={newMethod}
              onSelect={setNewMethod}
              total={total}
              currency={cur}
              mbwayPhone={newPhone}
              onMbwayPhoneChange={setNewPhone}
            />
            {changeError ? (
              <div className="mt-3 bg-[#FEF2F2] border border-[#FECACA] rounded-lg p-3 text-sm text-[#991B1B]" data-testid="my-order-payment-change-error">
                <Warning size={16} weight="fill" className="inline mr-1 -mt-1" /> {changeError}
              </div>
            ) : null}
            <button
              type="button"
              disabled={changing || !newMethod || newMethod === tipoPag}
              onClick={handleChange}
              data-testid="my-order-payment-change-btn"
              className="mt-4 inline-flex items-center gap-2 bg-[#0F1829] text-white rounded-full px-6 py-3 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {changing ? <SpinnerGap size={16} weight="bold" className="animate-spin" /> : <CheckCircle size={16} weight="bold" />}
              Confirmar alteração
            </button>
          </div>
        ) : null}
      </div>
    </div>
  );
}

function PaymentDetails({ tipoPag, pd, estado, total, cur, orderId, hashEncomenda, onRegenerate, regenerating, regenError, onPaymentConfirmed }) {
  if (estado === "PR" || estado === "EC" || estado === "DO") {
    return (
      <div className="bg-[#ECFDF5] border border-[#A7F3D0] rounded-2xl p-6 text-[#065F46]" data-testid="my-order-payment-paid">
        <CheckCircle size={24} weight="fill" className="mb-2" />
        <div className="font-semibold">Pagamento recebido</div>
        <div className="text-sm mt-1">Esta encomenda já foi paga. Obrigado!</div>
        <a href="https://ihavethepower.net" data-testid="my-order-back-to-site"
          className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[#0F1829] text-white rounded-md text-sm font-semibold hover:bg-[#002480] transition-colors">
          <ArrowLeft size={16} weight="bold" /> Voltar ao site
        </a>
      </div>
    );
  }

  if (tipoPag === "MB") {
    return <MultibancoBlock pd={pd} total={total} cur={cur} />;
  }

  if (tipoPag === "MW") {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6" data-testid="my-order-payment-mw">
        <div className="flex items-center gap-2 mb-3"><DeviceMobile size={20} weight="bold" /><h3 className="text-base font-semibold text-[#0F1829]">Pagamento por MBWay</h3></div>
        <p className="text-sm text-[#111827]">Foi enviado um pedido de pagamento para o número <strong data-testid="my-order-payment-mw-phone">{pd.NumeroTelemovel || "—"}</strong>.</p>
        <p className="text-xs text-[#6B7280] mt-2">Abra a app MBWay do seu banco e confirme o pagamento de <strong>{fmtMoney(total, cur)}</strong>.</p>
      </div>
    );
  }

  // Helper: bloco UI para métodos com URL externo (PP/CT/CP). O legacy nem
  // sempre popula o URL no checkout (e.g. EuPago timeout) — quando isso
  // acontece, oferecemos um botão "Tentar gerar link" que re-dispara o
  // `changePayment` no legacy (que faz nova chamada à gateway).
  const _renderExternalGatewayBlock = ({ testid, color, hoverColor, IconCmp, title, description, btnLabel, url, fallbackText }) => (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 text-center" data-testid={testid}>
      <div className="flex items-center justify-center gap-2 mb-3"><IconCmp size={22} weight="bold" className={color} /><h3 className="text-base font-semibold text-[#0F1829]">{title}</h3></div>
      <p className="text-sm text-[#6B7280] mb-4">{description}</p>
      {url ? (
        <a href={url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-btn`}
          className={`inline-flex items-center gap-2 ${color.replace("text-", "bg-")} hover:${hoverColor} text-white rounded-full px-6 py-3 text-sm font-semibold`}>
          <IconCmp size={18} weight="bold" /> {btnLabel}
        </a>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-[#991B1B]">{fallbackText}</p>
          <button onClick={onRegenerate} disabled={regenerating} data-testid={`${testid}-retry`}
            className="inline-flex items-center gap-2 bg-[#0F1829] hover:bg-[#1E293B] disabled:opacity-50 text-white rounded-full px-5 py-2 text-xs font-semibold">
            {regenerating ? <SpinnerGap size={14} className="animate-spin" /> : null}
            Tentar gerar link de pagamento
          </button>
          {regenError ? <p className="text-xs text-[#991B1B]">{regenError}</p> : null}
        </div>
      )}
    </div>
  );

  if (tipoPag === "PP") {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6" data-testid="my-order-payment-pp">
        <div className="flex items-center justify-center gap-2 mb-3">
          <PaypalLogo size={22} weight="bold" className="text-[#0070BA]" />
          <h3 className="text-base font-semibold text-[#0F1829]">Pagamento por PayPal</h3>
        </div>
        <p className="text-sm text-[#6B7280] mb-4 text-center">
          Total: <strong>{fmtMoney(total, cur)}</strong>. Use o botão PayPal abaixo para pagar com a sua conta ou cartão.
        </p>
        <MyOrderPaymentPaypal
          orderId={orderId}
          hashEncomenda={hashEncomenda}
          total={total}
          currency={cur}
          onPaid={onPaymentConfirmed}
        />
      </div>
    );
  }

  if (tipoPag === "CT") {
    const ctUrl = pd.UrlPagamentoCT || pd.urlPagamentoCT;
    return (
      <ExternalGatewayAutoRedirect
        url={ctUrl}
        testid="my-order-payment-ct"
        color="text-[#0F1829]"
        hoverColor="bg-[#1E293B]"
        IconCmp={CreditCard}
        title="Pagamento por Cartão de Crédito"
        description={<>Processado de forma segura pela EasyPay. Clique para pagar <strong>{fmtMoney(total, cur)}</strong>.</>}
        btnLabel="Pagar com cartão"
        onRegenerate={onRegenerate}
        regenerating={regenerating}
        regenError={regenError}
        fallbackText="Pagamento por cartão (EasyPay) está temporariamente indisponível. Use a secção abaixo para escolher outro método (Multibanco, MBWay ou PayPal)."
      />
    );
  }

  if (tipoPag === "CP") {
    const cpUrl = pd.UrlPagamentoCP || pd.urlPagamentoCP;
    return (
      <ExternalGatewayAutoRedirect
        url={cpUrl}
        testid="my-order-payment-cp"
        color="text-[#E60013]"
        hoverColor="bg-[#C4000F]"
        IconCmp={Wallet}
        title="Pagamento por Cofidis Pay"
        description={<>Divida o pagamento em prestações com Cofidis Pay. Clique para concluir o pagamento de <strong>{fmtMoney(total, cur)}</strong>.</>}
        btnLabel="Pagar com Cofidis Pay"
        onRegenerate={onRegenerate}
        regenerating={regenerating}
        regenError={regenError}
        fallbackText="Link Cofidis ainda não disponível — tente regerar."
      />
    );
  }

  if (tipoPag === "TB") {
    return (
      <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6" data-testid="my-order-payment-tb">
        <div className="flex items-center gap-2 mb-4"><Receipt size={20} weight="bold" /><h3 className="text-base font-semibold text-[#0F1829]">Transferência Bancária</h3></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-[#FAF8F3] rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">IBAN</div>
            <div className="text-base font-bold text-[#0F1829] mt-1 tabular-nums" data-testid="my-order-payment-tb-iban">PT50 0007 0447 0000 8940 0041 7</div>
          </div>
          <div className="bg-[#FAF8F3] rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">SWIFT / BIC</div>
            <div className="text-base font-bold text-[#0F1829] mt-1 tabular-nums" data-testid="my-order-payment-tb-swift">BESCPTPL</div>
          </div>
          <div className="bg-[#FAF8F3] rounded-xl p-4">
            <div className="text-[11px] uppercase tracking-wider text-[#6B7280]">Montante</div>
            <div className="text-base font-bold text-[#0F1829] mt-1 tabular-nums">{fmtMoney(total, cur)}</div>
          </div>
        </div>
        <p className="text-sm text-[#111827] mt-4">Indique no descritivo da transferência: <strong>ENC-{orderId}</strong>.</p>
        <p className="text-xs text-[#6B7280] mt-2">Após transferir, envie o comprovativo para <a href="mailto:ihavethepower@solfut.com" className="text-[#002480] underline">ihavethepower@solfut.com</a> indicando o nº da encomenda. A encomenda é processada após confirmação manual do pagamento (1-2 dias úteis).</p>
        <a href="https://ihavethepower.net" data-testid="my-order-back-to-site-tb"
          className="inline-flex items-center gap-2 mt-4 px-5 py-2.5 bg-[#0F1829] text-white rounded-md text-sm font-semibold hover:bg-[#002480] transition-colors">
          <ArrowLeft size={16} weight="bold" /> Voltar ao site
        </a>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 text-sm text-[#6B7280]" data-testid="my-order-payment-generic">
      Método de pagamento actual: <strong>{tipoPag || "—"}</strong>. Estado: <strong>{estado}</strong>.
    </div>
  );
}

/**
 * MB payment block — shows Entidade + Referência + Valor with a 1-click
 * copy button (Entidade+Referência concatenated, as most PT homebanking apps
 * expect) and a QR code in the SIBS "MB*Entidade*Ref*Valor" format. Portuguese
 * banking apps (CGD, Millennium, Santander, N26-PT, Revolut PT, etc.) can
 * scan this QR code to prefill the payment form.
 */
function MultibancoBlock({ pd, total, cur }) {
  const [copied, setCopied] = useState(false);
  const entidade = pd.EntidadeMB || "";
  const referencia = pd.ReferenciaMB || "";
  // SIBS QR payload convention used across PT banks for Multibanco references.
  const qrPayload = entidade && referencia
    ? `MB*${entidade}*${referencia}*${Number(total || 0).toFixed(2)}`
    : "";
  const copyValue = entidade && referencia ? `${entidade} ${referencia}` : "";

  const handleCopy = async () => {
    if (!copyValue) return;
    try {
      await navigator.clipboard.writeText(copyValue);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fallback for browsers/contexts without clipboard API (e.g. iframes
      // without the required allow-permission or pre-HTTPS tests).
      const el = document.createElement("textarea");
      el.value = copyValue; el.style.position = "fixed"; el.style.opacity = "0";
      document.body.appendChild(el); el.select();
      try { document.execCommand("copy"); setCopied(true); setTimeout(() => setCopied(false), 2500); }
      finally { document.body.removeChild(el); }
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6" data-testid="my-order-payment-mb">
      <div className="flex items-center gap-2 mb-4">
        <Barcode size={20} weight="bold" />
        <h3 className="text-base font-semibold text-[#0F1829]">Pagamento por Referência Multibanco</h3>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-5 items-start">
        <div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-[#FAF8F3] rounded-xl p-3 sm:p-4 min-w-0">
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Entidade</div>
              <div className="text-base sm:text-xl font-bold text-[#0F1829] mt-1 tabular-nums break-all leading-tight" data-testid="my-order-payment-mb-entidade">{entidade || "—"}</div>
            </div>
            <div className="bg-[#FAF8F3] rounded-xl p-3 sm:p-4 min-w-0">
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Referência</div>
              <div className="text-base sm:text-xl font-bold text-[#0F1829] mt-1 tabular-nums break-all leading-tight" data-testid="my-order-payment-mb-ref">{referencia || "—"}</div>
            </div>
            <div className="bg-[#FAF8F3] rounded-xl p-3 sm:p-4 min-w-0">
              <div className="text-xs uppercase tracking-wider text-[#6B7280]">Montante</div>
              <div className="text-base sm:text-xl font-bold text-[#0F1829] mt-1 tabular-nums break-all leading-tight">{fmtMoney(total, cur)}</div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleCopy}
            disabled={!copyValue}
            data-testid="my-order-payment-mb-copy"
            className="mt-4 inline-flex items-center gap-2 bg-[#0F1829] text-white rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#1E293B] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {copied ? (
              <><Check size={16} weight="bold" /> Copiado!</>
            ) : (
              <><Copy size={16} weight="bold" /> Copiar Entidade + Referência</>
            )}
          </button>

          <p className="text-xs text-[#6B7280] mt-3">
            A referência fica activa durante 72 h. Pode pagar em qualquer caixa Multibanco ou no homebanking — ou usar o QR code ao lado com a app do seu banco.
          </p>
        </div>

        {/* QR code visible only when we actually have entity + reference */}
        {qrPayload ? (
          <div className="bg-white border border-[#E5E7EB] rounded-xl p-4 flex flex-col items-center justify-self-center md:justify-self-end" data-testid="my-order-payment-mb-qr">
            <QRCodeSVG value={qrPayload} size={144} level="M" includeMargin={false} />
            <span className="text-[10px] uppercase tracking-wider text-[#6B7280] mt-2">Scan com app do banco</span>
          </div>
        ) : null}
      </div>
    </div>
  );
}


/**
 * Bloco para gateways URL-based (CT/CP). Mostra o botão CTA quando o URL
 * existe (utilizador clica para sair). Quando o URL não existe (gateway
 * falhou no checkout), mostra fallback com botão "Tentar gerar link".
 */
function ExternalGatewayAutoRedirect({
  url, testid, color, hoverColor, IconCmp, title, description, btnLabel,
  onRegenerate, regenerating, regenError, fallbackText,
}) {
  return (
    <div className="bg-white rounded-2xl border border-[#E5E7EB] p-6 text-center" data-testid={testid}>
      <div className="flex items-center justify-center gap-2 mb-3">
        <IconCmp size={22} weight="bold" className={color} />
        <h3 className="text-base font-semibold text-[#0F1829]">{title}</h3>
      </div>
      <p className="text-sm text-[#6B7280] mb-4">{description}</p>
      {url ? (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`${testid}-btn`}
          className={`inline-flex items-center gap-2 ${color.replace("text-", "bg-")} hover:${hoverColor} text-white rounded-full px-6 py-3 text-sm font-semibold`}
        >
          <IconCmp size={18} weight="bold" /> {btnLabel}
        </a>
      ) : (
        <div className="space-y-2">
          <p className="text-xs text-[#991B1B]">{fallbackText}</p>
          <button onClick={onRegenerate} disabled={regenerating} data-testid={`${testid}-retry`}
            className="inline-flex items-center gap-2 bg-[#0F1829] hover:bg-[#1E293B] disabled:opacity-50 text-white rounded-full px-5 py-2 text-xs font-semibold">
            {regenerating ? <SpinnerGap size={14} className="animate-spin" /> : null}
            Tentar gerar link de pagamento
          </button>
          {regenError ? <p className="text-xs text-[#991B1B]">{regenError}</p> : null}
        </div>
      )}
    </div>
  );
}
